
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle, GraduationCap, FileText, Users, Download, ChevronRight, Star, Globe, BookOpen, Award, ArrowLeft, User, Mail, Phone, MapPin, School, Lock, CreditCard, LayoutDashboard } from 'lucide-react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TabNavigation from './components/TabNavigation';
import RegistrationForm from './components/RegistrationForm';
import SkillSelection from './components/SkillSelection';
import CVAssistant from './components/CVAssistant';
import AdminView from './components/AdminView';
import UserDashboard from './components/UserDashboard';
import PaymentModal from './components/PaymentModal';

const SKILLS_LIST = [
  { id: 'word', name: 'Microsoft Word', icon: 'ri-file-word-line', color: 'from-blue-500 to-blue-600', description: 'Learn document creation, formatting, and professional layouts' },
  { id: 'excel', name: 'Microsoft Excel', icon: 'ri-file-excel-line', color: 'from-green-500 to-green-600', description: 'Master spreadsheets, formulas, data analysis, and charts' },
  { id: 'powerpoint', name: 'Microsoft PowerPoint', icon: 'ri-file-ppt-line', color: 'from-orange-500 to-orange-600', description: 'Create compelling presentations and visual storytelling' },
  { id: 'basic', name: 'Basic Computer Skills', icon: 'ri-computer-line', color: 'from-purple-500 to-purple-600', description: 'Essential computer operations, file management, and navigation' },
  { id: 'internet', name: 'Internet & Email', icon: 'ri-globe-line', color: 'from-teal-500 to-teal-600', description: 'Web browsing, online safety, email communication, and research' },
  { id: 'cv', name: 'CV Writing', icon: 'ri-file-user-line', color: 'from-indigo-500 to-indigo-600', description: 'Craft professional resumes and cover letters that stand out' },
  { id: 'freelancing', name: 'Freelancing Basics', icon: 'ri-briefcase-line', color: 'from-pink-500 to-pink-600', description: 'Start your freelance career, client management, and pricing' },
  { id: 'ai', name: 'AI Tools & Automation', icon: 'ri-robot-line', color: 'from-red-500 to-red-600', description: 'Leverage AI tools for productivity and automation' },
];

const COURSE_PRICE = 2;
const CV_DOWNLOAD_PRICE = 3;

const LearningHubPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'register' | 'skills' | 'dashboard' | 'cv' | 'admin'>('register');
  const [currentStep, setCurrentStep] = useState(1);
  const [registeredStudents, setRegisteredStudents] = useState<any[]>([]);
  const [selectedStudentSkills, setSelectedStudentSkills] = useState<{ [key: string]: string[] }>({});
  const [registrationData, setRegistrationData] = useState<any>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [savedProfiles, setSavedProfiles] = useState<any[]>([]);
  const [enrolledCourses, setEnrolledCourses] = useState<{ [email: string]: string[] }>({});
  const [paidForCV, setPaidForCV] = useState<{ [email: string]: boolean }>({});
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentType, setPaymentType] = useState<'enrollment' | 'cv_download'>('enrollment');

  useEffect(() => {
    const saved = localStorage.getItem('learningHubProfiles');
    if (saved) {
      try {
        setSavedProfiles(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved profiles');
      }
    }
  }, []);

  useEffect(() => {
    const savedStudents = localStorage.getItem('registeredStudents');
    if (savedStudents) {
      try {
        setRegisteredStudents(JSON.parse(savedStudents));
      } catch (e) {
        console.error('Failed to parse registered students');
      }
    }
  }, []);

  useEffect(() => {
    const savedEnrollments = localStorage.getItem('enrolledCourses');
    if (savedEnrollments) {
      try {
        setEnrolledCourses(JSON.parse(savedEnrollments));
      } catch (e) {
        console.error('Failed to parse enrollments');
      }
    }
  }, []);

  useEffect(() => {
    const savedCVPayments = localStorage.getItem('paidForCV');
    if (savedCVPayments) {
      try {
        setPaidForCV(JSON.parse(savedCVPayments));
      } catch (e) {
        console.error('Failed to parse CV payments');
      }
    }
  }, []);

  const handleRegistration = (data: any) => {
    setRegistrationData(data);
    const newStudents = [...registeredStudents, data];
    setRegisteredStudents(newStudents);
    localStorage.setItem('registeredStudents', JSON.stringify(newStudents));
    setCurrentStep(2);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 4000);
  };

  const handleSkillSelection = (studentEmail: string, skills: string[]) => {
    setSelectedStudentSkills(prev => ({ ...prev, [studentEmail]: skills }));
    const newProfiles = [...savedProfiles, {
      ...registrationData,
      skills,
      registeredAt: new Date().toISOString(),
    }];
    setSavedProfiles(newProfiles);
    localStorage.setItem('learningHubProfiles', JSON.stringify(newProfiles));
    setCurrentStep(3);
    setActiveTab('dashboard');
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 4000);
  };

  const handlePaymentSuccess = (txRef: string, courses: string[], _courseNames: string[]) => {
    if (paymentType === 'enrollment') {
      const userEmail = registrationData?.email;
      if (userEmail) {
        const updatedEnrollments = { ...enrolledCourses };
        updatedEnrollments[userEmail] = [...(updatedEnrollments[userEmail] || []), ...courses];
        setEnrolledCourses(updatedEnrollments);
        localStorage.setItem('enrolledCourses', JSON.stringify(updatedEnrollments));
        
        // Also update selected skills
        const allSkills = [...(selectedStudentSkills[userEmail] || []), ...courses];
        const uniqueSkills = [...new Set(allSkills)];
        setSelectedStudentSkills(prev => ({ ...prev, [userEmail]: uniqueSkills }));
        
        // Save enrollment record
        const enrollmentRecord = {
          email: userEmail,
          tx_ref: txRef,
          courses,
          paidAt: new Date().toISOString(),
          amount: courses.length * COURSE_PRICE,
        };
        const existingEnrollments = JSON.parse(localStorage.getItem('enrollmentRecords') || '[]');
        existingEnrollments.push(enrollmentRecord);
        localStorage.setItem('enrollmentRecords', JSON.stringify(existingEnrollments));
      }
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);
    } else {
      const userEmail = registrationData?.email;
      if (userEmail) {
        const updatedCVPayments = { ...paidForCV };
        updatedCVPayments[userEmail] = true;
        setPaidForCV(updatedCVPayments);
        localStorage.setItem('paidForCV', JSON.stringify(updatedCVPayments));
      }
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);
    }
  };

  const handleEnrollClick = (courseIds: string[]) => {
    setPaymentType('enrollment');
    setPaymentModalOpen(true);
  };

  const handleReset = () => {
    setCurrentStep(1);
    setRegistrationData(null);
    setShowSuccess(false);
    setActiveTab('register');
  };

  const getUserEnrolledCourses = (email: string) => {
    return enrolledCourses[email] || [];
  };

  const getStudentSkills = (email: string) => {
    return selectedStudentSkills[email] || [];
  };

  const isCourseEnrolled = (email: string, courseId: string) => {
    return (enrolledCourses[email] || []).includes(courseId);
  };

  const hasPaidForCV = (email: string) => {
    return paidForCV[email] || false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-blue-900 via-blue-800 to-red-700 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent"></div>
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-red-500/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center text-white"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
              <GraduationCap className="w-4 h-4" />
              <span>Professional Learning Platform for You</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold mb-6">
              Learning Hub
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 leading-relaxed max-w-2xl mx-auto">
              Master essential computer skills and build your career with our comprehensive training programs
            </p>
            {registrationData && (
              <div className="mt-6 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm">
                <User className="w-4 h-4" />
                <span>Welcome, {registrationData.fullName}</span>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            {[
              { step: 1, label: 'Register', icon: User, tab: 'register' as const },
              { step: 2, label: 'Choose Skills', icon: BookOpen, tab: 'skills' as const },
              { step: 3, label: 'My Dashboard', icon: LayoutDashboard, tab: 'dashboard' as const },
              { step: 4, label: 'CV Assistant', icon: FileText, tab: 'cv' as const },
            ].map((item, index) => (
              <div key={item.step} className="flex items-center gap-3 md:gap-4">
                <button
                  onClick={() => {
                    if (item.tab === 'skills' && !registrationData) return;
                    if ((item.tab === 'dashboard' || item.tab === 'cv') && !registrationData) return;
                    setActiveTab(item.tab);
                    setCurrentStep(item.step);
                  }}
                  disabled={(item.tab === 'skills' || item.tab === 'dashboard' || item.tab === 'cv') && !registrationData}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                    currentStep === item.step
                      ? 'bg-gradient-to-r from-blue-600 to-red-600 text-white shadow-lg'
                      : currentStep > item.step
                      ? 'bg-green-50 text-green-700 border-2 border-green-200'
                      : 'bg-gray-50 text-gray-400 border-2 border-gray-200'
                  } ${!registrationData && item.step > 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                    currentStep === item.step
                      ? 'bg-white/20'
                      : currentStep > item.step
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {currentStep > item.step ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <item.icon className="w-4 h-4" />
                    )}
                  </div>
                  <span className="font-semibold text-xs md:text-sm whitespace-nowrap">{item.label}</span>
                </button>
                {index < 3 && (
                  <ChevronRight className={`w-4 h-4 ${currentStep > item.step ? 'text-green-500' : 'text-gray-300'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-6">
          <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>
      </section>

      {/* Success Animation */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.9 }}
            className="fixed top-24 right-6 z-50 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 max-w-sm"
          >
            <CheckCircle className="w-6 h-6 flex-shrink-0" />
            <div>
              <p className="font-bold">Success!</p>
              <p className="text-sm text-green-50">Your transaction was completed successfully.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onPaymentSuccess={handlePaymentSuccess}
        selectedCourses={paymentType === 'enrollment' ? selectedStudentSkills[registrationData?.email || ''] || [] : []}
        skillsList={SKILLS_LIST}
        userEmail={registrationData?.email || ''}
        userName={registrationData?.fullName || ''}
        type={paymentType}
      />

      {/* Main Content */}
      <main className="py-12">
        <div className="container mx-auto px-6">
          <AnimatePresence mode="wait">
            {activeTab === 'register' && (
              <motion.div
                key="register"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                <RegistrationForm
                  onRegister={handleRegistration}
                  currentStep={currentStep}
                />
              </motion.div>
            )}

            {activeTab === 'skills' && registrationData && (
              <motion.div
                key="skills"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                <SkillSelection
                  studentEmail={registrationData.email}
                  studentName={registrationData.fullName}
                  onSkillsSelected={handleSkillSelection}
                  selectedSkills={getStudentSkills(registrationData.email)}
                  skillsList={SKILLS_LIST}
                />
              </motion.div>
            )}

            {activeTab === 'dashboard' && registrationData && (
              <motion.div
                key="dashboard"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                <UserDashboard
                  user={registrationData}
                  skillsList={SKILLS_LIST}
                  enrolledCourses={getUserEnrolledCourses(registrationData.email)}
                  selectedSkills={getStudentSkills(registrationData.email)}
                  hasPaidForCV={hasPaidForCV(registrationData.email)}
                  onEnrollClick={handleEnrollClick}
                  onPaymentSuccess={handlePaymentSuccess}
                  setPaymentModalOpen={setPaymentModalOpen}
                  currentStep={currentStep}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
              </motion.div>
            )}

            {activeTab === 'cv' && (
              <motion.div
                key="cv"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                <CVAssistant
                  savedProfiles={savedProfiles}
                  registrationData={registrationData}
                  hasPaidForCV={registrationData ? hasPaidForCV(registrationData.email) : false}
                  onPaymentRequired={() => {
                    if (!registrationData) return;
                    setPaymentType('cv_download');
                    setPaymentModalOpen(true);
                  }}
                />
              </motion.div>
            )}

            {activeTab === 'admin' && (
              <motion.div
                key="admin"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                <AdminView
                  students={registeredStudents}
                  getStudentSkills={getStudentSkills}
                  savedProfiles={savedProfiles}
                  enrolledCourses={enrolledCourses}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LearningHubPage;
